/**
 * SuggestedRuleCard Component
 *
 * Card component for displaying a suggested classification rule.
 * Shows pattern, target account, LLM reasoning, and action buttons.
 *
 * Features:
 * - Pattern display with regex highlighting
 * - Target account path display
 * - LLM reasoning section
 * - Test match count badge
 * - Original transaction info
 * - Approve/Reject action buttons
 * - Loading state during actions
 */

import { useState } from 'react';
import clsx from 'clsx';
import {
  CheckCircle,
  XCircle,
  Sparkles,
  Hash,
  FileText,
  Calendar,
  Loader2,
  Edit2,
  Code,
  FolderTree,
  AlertCircle,
} from 'lucide-react';
import type { SuggestedRule, Account } from '@/types/accounting';

export interface SuggestedRuleCardProps {
  /** The suggested rule to display */
  rule: SuggestedRule;
  /** Available accounts for override */
  accounts: Account[];
  /** Callback when the rule is approved */
  onApprove: (
    id: number,
    overrides?: {
      name?: string;
      patternOverride?: string;
      targetAccountIdOverride?: number;
    }
  ) => Promise<void>;
  /** Callback when the rule is rejected */
  onReject: (id: number) => Promise<void>;
  /** Whether actions are disabled */
  disabled?: boolean;
}

/**
 * Format a date string for display
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Validate a regex pattern
 */
function validatePattern(pattern: string): { valid: boolean; error?: string } {
  if (!pattern.trim()) {
    return { valid: false, error: 'Pattern is required' };
  }
  try {
    new RegExp(pattern, 'i');
    return { valid: true };
  } catch {
    return { valid: false, error: 'Invalid regex pattern' };
  }
}

/**
 * SuggestedRuleCard displays a suggested rule with approve/reject actions.
 *
 * @example
 * <SuggestedRuleCard
 *   rule={suggestedRule}
 *   onApprove={handleApprove}
 *   onReject={handleReject}
 * />
 */
export function SuggestedRuleCard({
  rule,
  accounts,
  onApprove,
  onReject,
  disabled = false,
}: SuggestedRuleCardProps) {
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [customName, setCustomName] = useState('');
  const [patternEdit, setPatternEdit] = useState(rule.pattern);
  const [targetAccountEdit, setTargetAccountEdit] = useState(rule.targetAccountId);
  const [patternValidation, setPatternValidation] = useState<{ valid: boolean; error?: string }>({ valid: true });

  const isLoading = isApproving || isRejecting;
  const isDisabled = disabled || isLoading;

  const handlePatternChange = (pattern: string) => {
    setPatternEdit(pattern);
    setPatternValidation(validatePattern(pattern));
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setPatternEdit(rule.pattern);
    setTargetAccountEdit(rule.targetAccountId);
    setCustomName('');
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setPatternEdit(rule.pattern);
    setTargetAccountEdit(rule.targetAccountId);
    setCustomName('');
    setPatternValidation({ valid: true });
  };

  const handleApprove = async () => {
    if (isEditing && !patternValidation.valid) {
      return;
    }

    setIsApproving(true);
    try {
      const hasOverrides =
        isEditing &&
        (customName.trim() ||
          patternEdit !== rule.pattern ||
          targetAccountEdit !== rule.targetAccountId);

      const overrides = hasOverrides
        ? {
            name: customName.trim() || undefined,
            patternOverride: patternEdit !== rule.pattern ? patternEdit : undefined,
            targetAccountIdOverride:
              targetAccountEdit !== rule.targetAccountId ? targetAccountEdit : undefined,
          }
        : undefined;

      await onApprove(rule.id, overrides);
    } finally {
      setIsApproving(false);
      setIsEditing(false);
    }
  };

  const handleReject = async () => {
    setIsRejecting(true);
    try {
      await onReject(rule.id);
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <div
      className={clsx(
        'bg-white rounded-lg border border-gray-200 overflow-hidden',
        'transition-shadow hover:shadow-md',
        isLoading && 'opacity-70'
      )}
      data-testid="suggested-rule-card"
      data-rule-id={rule.id}
    >
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Suggested Rule</span>
          {rule.testMatchCount > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
              title={`This pattern matches ${rule.testMatchCount} transaction${rule.testMatchCount !== 1 ? 's' : ''}`}
            >
              <Hash size={12} />
              {rule.testMatchCount} match{rule.testMatchCount !== 1 ? 'es' : ''}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500">
          {formatDate(rule.createdAt)}
        </span>
      </div>

      {isEditing ? (
        /* Edit Mode */
        <div className="p-4 space-y-4">
          {/* Rule Name */}
          <div>
            <label
              htmlFor={`rule-name-${rule.id}`}
              className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1"
            >
              Rule Name (optional)
            </label>
            <input
              id={`rule-name-${rule.id}`}
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="e.g., Grocery Stores"
              disabled={isApproving}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="edit-rule-name-input"
            />
          </div>

          {/* Pattern */}
          <div>
            <label
              htmlFor={`pattern-${rule.id}`}
              className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider mb-1"
            >
              <Code size={12} />
              Pattern (regex)
            </label>
            <div className="relative">
              <input
                id={`pattern-${rule.id}`}
                type="text"
                value={patternEdit}
                onChange={(e) => handlePatternChange(e.target.value)}
                disabled={isApproving}
                className={clsx(
                  'w-full px-3 py-2 pr-8 text-sm font-mono border rounded-md focus:outline-none focus:ring-2',
                  patternValidation.valid
                    ? 'border-gray-300 focus:ring-blue-500'
                    : 'border-red-300 focus:ring-red-500'
                )}
                data-testid="edit-pattern-input"
              />
              {patternValidation.valid && patternEdit && (
                <CheckCircle className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" data-testid="pattern-valid-icon" />
              )}
            </div>
            {!patternValidation.valid && patternValidation.error && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1" data-testid="pattern-error">
                <AlertCircle size={12} />
                {patternValidation.error}
              </p>
            )}
          </div>

          {/* Target Account */}
          <div>
            <label
              htmlFor={`target-${rule.id}`}
              className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider mb-1"
            >
              <FolderTree size={12} />
              Target Account
            </label>
            <select
              id={`target-${rule.id}`}
              value={targetAccountEdit}
              onChange={(e) => setTargetAccountEdit(Number(e.target.value))}
              disabled={isApproving}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="edit-target-account-select"
            >
              {accounts
                .filter((a) => a.accountType === 'expense' || a.accountType === 'income')
                .map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.fullPath}
                  </option>
                ))}
            </select>
          </div>

          {/* Original Transaction Reference */}
          <div className="px-3 py-2 bg-gray-50 rounded-md text-sm text-gray-600">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Original:
            </span>{' '}
            {rule.transactionDescription}
          </div>

          {/* Edit Mode Actions */}
          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={handleApprove}
              disabled={isDisabled || !patternValidation.valid}
              className={clsx(
                'flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md',
                'bg-green-600 text-white hover:bg-green-700',
                'focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
              data-testid="save-approve-button"
            >
              {isApproving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <CheckCircle size={16} />
              )}
              {isApproving ? 'Saving...' : 'Save & Approve'}
            </button>
            <button
              type="button"
              onClick={handleCancelEdit}
              disabled={isDisabled}
              className={clsx(
                'px-3 py-2 text-sm font-medium rounded-md',
                'bg-gray-100 text-gray-700 hover:bg-gray-200',
                'focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2'
              )}
              data-testid="cancel-edit-button"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        /* Display Mode */
        <>
          {/* Pattern */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <Code size={14} className="text-gray-400" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pattern
              </span>
            </div>
            <code
              className="block px-3 py-2 bg-gray-900 rounded text-green-400 text-sm font-mono break-all"
              data-testid="rule-pattern"
            >
              /{rule.pattern}/i
            </code>
          </div>

          {/* Target Account */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2 text-sm">
              <FolderTree size={14} className="text-gray-400" />
              <span className="text-gray-500">Target:</span>
              <span
                className="font-medium text-gray-900"
                data-testid="target-account"
              >
                {rule.targetAccountPath}
              </span>
            </div>
          </div>

      {/* LLM Reasoning */}
      {rule.llmReasoning && (
        <div className="px-4 py-3 border-b border-gray-100 bg-purple-50/50">
          <div className="flex items-start gap-2">
            <Sparkles
              size={16}
              className="text-purple-500 flex-shrink-0 mt-0.5"
            />
            <div>
              <span className="text-xs font-medium text-purple-700 uppercase tracking-wider">
                AI Reasoning
              </span>
              <p
                className="mt-1 text-sm text-gray-700"
                data-testid="llm-reasoning"
              >
                {rule.llmReasoning}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Original Transaction */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-start gap-2">
          <FileText size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Original Transaction
            </span>
            <p
              className="mt-1 text-sm text-gray-700 truncate"
              title={rule.transactionDescription}
              data-testid="transaction-description"
            >
              {rule.transactionDescription}
            </p>
            <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
              <Calendar size={12} />
              <span data-testid="transaction-date">
                {formatDate(rule.transactionDate)}
              </span>
            </div>
          </div>
        </div>
      </div>

          {/* Actions */}
          <div className="px-4 py-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleStartEdit}
                disabled={isDisabled}
                className={clsx(
                  'inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md',
                  'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                data-testid="edit-button"
              >
                <Edit2 size={16} />
                Edit
              </button>
              <button
                type="button"
                onClick={handleApprove}
                disabled={isDisabled}
                className={clsx(
                  'flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md',
                  'bg-green-600 text-white hover:bg-green-700',
                  'focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                data-testid="approve-button"
              >
                {isApproving ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CheckCircle size={16} />
                )}
                {isApproving ? 'Approving...' : 'Approve'}
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={isDisabled}
                className={clsx(
                  'inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md',
                  'bg-white text-red-600 border border-red-300 hover:bg-red-50',
                  'focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                data-testid="reject-button"
              >
                {isRejecting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <XCircle size={16} />
                )}
                {isRejecting ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SuggestedRuleCard;
